import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Container = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.95rem;
  color: #4b5563;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  color: #e5e7eb;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
`;

const TextArea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.9rem;
  min-height: 80px;
  resize: vertical;
`;

const HelperText = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #9ca3af;
`;

const ErrorText = styled.p`
  margin: 0.2rem 0 0;
  font-size: 0.8rem;
  color: #f97373;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const PrimaryButton = styled.button`
  padding: 0.6rem 1.4rem;
  border-radius: 999px;
  border: none;
  background-color: #111827;
  color: #f9fafb;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.05s ease;

  &:hover {
    background-color: #1f2937;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
    transform: none;
  }
`;

const DepositHighlight = styled.p`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 600;
  color: #facc15;
`;

const StatusMessage = styled.p<{ $type: 'success' | 'error' }>`
  margin-top: 0.75rem;
  font-size: 0.9rem;
  color: ${({ $type }) => ($type === 'success' ? '#22c55e' : '#f97373')};
`;

const DisclaimerBox = styled.div`
  margin-top: 1.25rem;
  padding: 1rem 1rem;
  border-radius: 8px;
  background: #020617;
  border: 1px solid #4b5563;
`;

const DisclaimerTitle = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #f97316;
`;

const DisclaimerText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: #e5e7eb;
`;

const CheckboxRow = styled.div`
  margin-top: 0.75rem;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #e5e7eb;
`;

const ChoiceBox = styled.div`
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  background: #020617;
  border: 1px solid #4b5563;
`;

const ChoiceTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.1rem;
  color: #e5e7eb;
`;

const ChoiceText = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: #9ca3af;
`;

const ChoiceButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const SecondaryChoiceButton = styled.button`
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  border: 1px solid #4b5563;
  background-color: transparent;
  color: #e5e7eb;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.05s ease;

  &:hover {
    background-color: #111827;
    border-color: #9ca3af;
    transform: translateY(-1px);
  }
`;

type BookingFormState = {
  name: string;
  phone: string;
  email: string;
  placement: string;
  size: string;
  styleIdea: string;
  budget: string;
  availability: string;
  extraNotes: string;
};

const Booking: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const depositAmount = (user?.profile?.depositAmount || '').trim();
  const [bookingKind, setBookingKind] = useState<'appointment' | 'consultation' | null>(null);
  const [ackConsultationInfo, setAckConsultationInfo] = useState(false);
  const [ackError, setAckError] = useState('');
  const [form, setForm] = useState<BookingFormState>({
    name: '',
    phone: '',
    email: '',
    placement: '',
    size: '',
    styleIdea: '',
    budget: '',
    availability: '',
    extraNotes: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingFormState, string>>>({});
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [readDisclaimer, setReadDisclaimer] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState('');
  const [designFiles, setDesignFiles] = useState<FileList | null>(null);
  const [wantsPhoneConsultation, setWantsPhoneConsultation] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
    setStatus(null);
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setDesignFiles(files && files.length > 0 ? files : null);
    setStatus(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    setDisclaimerError('');
    setAckError('');

    const newErrors: Partial<Record<keyof BookingFormState, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Please enter your name.';
    if (!form.phone.trim()) newErrors.phone = 'Please enter your phone number.';
    if (!form.email.trim()) newErrors.email = 'Please enter your email.';
    if (!form.placement.trim()) newErrors.placement = 'Tell us where on your body this tattoo will go.';
    if (!form.size.trim()) newErrors.size = 'Rough size helps with pricing and timing.';
    if (!form.styleIdea.trim()) newErrors.styleIdea = 'Share a quick idea, style, or reference description.';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setStatus({ type: 'error', message: 'Please fix the highlighted fields.' });
      return;
    }

    if (!bookingKind) {
      setAckError('Please choose whether you are booking a consultation or an appointment above.');
      setStatus({ type: 'error', message: 'Please choose consultation or appointment first.' });
      return;
    }

    if (!readDisclaimer) {
      setDisclaimerError('You must confirm you have read the note above before submitting.');
      setStatus({ type: 'error', message: 'Please confirm you have read the important note.' });
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', form.name.trim());
      formData.append('email', form.email.trim());
      formData.append('phone', form.phone.trim());
      formData.append('placement', form.placement.trim());
      formData.append('size', form.size.trim());
      formData.append('styleIdea', form.styleIdea.trim());
      formData.append('budget', form.budget.trim());
      formData.append('availability', form.availability.trim());
      formData.append('extraNotes', form.extraNotes.trim());
      formData.append('readDisclaimer', 'true');
      formData.append('kind', bookingKind);
      formData.append('wantsPhoneConsultation', wantsPhoneConsultation ? 'true' : 'false');

      if (designFiles) {
        Array.from(designFiles).forEach(file => {
          formData.append('designFiles', file);
        });
      }

      const response = await api.post('/bookings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const baseMessage =
        bookingKind === 'consultation'
          ? "You have successfully booked a consultation. We'll review your request and get back to you at the contact details you provided."
          : "You have successfully booked an appointment. We'll review your request and confirm your time at the contact details you provided.";

      const depositMessage =
        bookingKind === 'appointment' && depositAmount
          ? ` A deposit of $${depositAmount} is required to fully secure your appointment. We'll follow up with how to send it.`
          : bookingKind === 'consultation'
          ? ' Consultations are free to book. If you decide to move forward with a tattoo, a deposit may be required at that time.'
          : '';

      setStatus({
        type: 'success',
        message: baseMessage + depositMessage,
      });

      // If this is an appointment and there is a deposit amount, send them to the deposit payment page
      if (bookingKind === 'appointment' && depositAmount && response?.data?._id) {
        navigate(`/pay/deposit/${response.data._id}`);
        return;
      }

      // For consultations and appointments without a deposit, send to a simple confirmation page
      navigate(`/booking/confirmed?kind=${bookingKind}`);
    } catch (err: any) {
      console.error('Failed to submit booking request', err);
      const message = err?.response?.data?.message || 'Something went wrong sending your request.';
      setStatus({ type: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>Book an appointment or consultation</Title>
      <Subtitle>
        Tell us a bit about what you have in mind and how to reach you. Start by choosing whether you&apos;d like a
        consultation or to go straight to booking an appointment.
      </Subtitle>

      <Form onSubmit={handleSubmit}>
        {depositAmount && (
          <DepositHighlight>
            To book an appointment is a ${depositAmount} deposit. Consultations are free to book!
          </DepositHighlight>
        )}

        {!bookingKind && (
          <ChoiceBox>
              <ChoiceTitle>First, a quick note about consultations</ChoiceTitle>
              <ChoiceText>
                Consultations are always preferred before an appointment is set, especially for larger or custom designs.
                If you&apos;re booking for a smaller tattoo or a very straightforward idea, a consultation might not be needed.
                It&apos;s always a good idea to talk with the artist before any tattoo though!
              </ChoiceText>
              <CheckboxRow>
                <input
                  id="ackConsultationInfo"
                  type="checkbox"
                  checked={ackConsultationInfo}
                  onChange={e => {
                    setAckConsultationInfo(e.target.checked);
                    if (e.target.checked) setAckError('');
                  }}
                />
                <label htmlFor="ackConsultationInfo">
                  I have read and understand when consultations are recommended before booking.
                </label>
              </CheckboxRow>
              {ackError && <ErrorText>{ackError}</ErrorText>}
              <ChoiceButtons>
                <SecondaryChoiceButton
                  type="button"
                  onClick={() => {
                    if (!ackConsultationInfo) {
                      setAckError('Please confirm you have read the note above before choosing.');
                      return;
                    }
                    setBookingKind('consultation');
                  }}
                >
                  Book a consultation
                </SecondaryChoiceButton>
                <SecondaryChoiceButton
                  type="button"
                  onClick={() => {
                    if (!ackConsultationInfo) {
                      setAckError('Please confirm you have read the note above before choosing.');
                      return;
                    }
                    setBookingKind('appointment');
                  }}
                >
                  Book an appointment
                </SecondaryChoiceButton>
              </ChoiceButtons>
          </ChoiceBox>
        )}

        {bookingKind && (
          <>
            <CheckboxRow>
              <input
                id="wantsPhoneConsultation"
                type="checkbox"
                checked={wantsPhoneConsultation}
                onChange={e => setWantsPhoneConsultation(e.target.checked)}
              />
              <label htmlFor="wantsPhoneConsultation">
                I&apos;d prefer this to be a phone consultation (only check this box for consultations).
              </label>
            </CheckboxRow>
            <HelperText>
              This helps the artist know whether to schedule an in-person consult or plan to call you instead.
            </HelperText>

            <FieldGroup>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                value={form.name}
                onChange={handleChange}
                autoComplete="name"
              />
              {errors.name && <ErrorText>{errors.name}</ErrorText>}
            </FieldGroup>

        <FieldGroup>
          <Label htmlFor="phone">Phone</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="e.g. (555) 123-4567"
            value={form.phone}
            onChange={handleChange}
            autoComplete="tel"
          />
          {errors.phone && <ErrorText>{errors.phone}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
          />
          {errors.email && <ErrorText>{errors.email}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="placement">Placement</Label>
          <Input
            id="placement"
            name="placement"
            placeholder="e.g. left forearm, outer thigh, ribs"
            value={form.placement}
            onChange={handleChange}
          />
          {errors.placement && <ErrorText>{errors.placement}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="size">Rough size</Label>
          <Input
            id="size"
            name="size"
            placeholder="e.g. around 4 inches tall, palm-sized, half sleeve"
            value={form.size}
            onChange={handleChange}
          />
          {errors.size && <ErrorText>{errors.size}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="styleIdea">Style / idea</Label>
          <TextArea
            id="styleIdea"
            name="styleIdea"
            placeholder="Share the concept, style (black & grey, fine line, color, etc.), and any reference ideas."
            value={form.styleIdea}
            onChange={handleChange}
          />
          {errors.styleIdea && <ErrorText>{errors.styleIdea}</ErrorText>}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="budget">Budget (optional)</Label>
          <Input
            id="budget"
            name="budget"
            placeholder="e.g. around $300/$500"
            value={form.budget}
            onChange={handleChange}
          />
          <HelperText>
            Sharing a range helps us suggest the right size and approach, but you can leave this blank if youre not
            sure yet.
          </HelperText>
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="availability">Day / time preferences (optional)</Label>
          <Input
            id="availability"
            name="availability"
            placeholder="e.g. weekday evenings, Saturdays only, flexible"
            value={form.availability}
            onChange={handleChange}
          />
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="designFiles">Reference photos (optional)</Label>
          <Input
            id="designFiles"
            name="designFiles"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilesChange}
          />
          <HelperText>
            You can attach reference images or healed tattoo photos to help plan your piece. These are for your artist only.
          </HelperText>
          {designFiles && designFiles.length > 0 && (
            <HelperText>
              {designFiles.length} file{designFiles.length > 1 ? 's' : ''} selected.
            </HelperText>
          )}
        </FieldGroup>

        <FieldGroup>
          <Label htmlFor="extraNotes">Anything else we should know? (optional)</Label>
          <TextArea
            id="extraNotes"
            name="extraNotes"
            placeholder="Share any accessibility needs, cover-up information, healed work in the area, or other details you think are important."
            value={form.extraNotes}
            onChange={handleChange}
          />
        </FieldGroup>

        <DisclaimerBox>
          <DisclaimerTitle>PLEASE READ THIS!!!</DisclaimerTitle>
          <DisclaimerText>
            Please be aware if you are setting an appointment without any consultation (usually done if your tattoo is
            smaller and/or fairly simple) certain details may have to be altered in order to give you a high quality
            tattoo that will withstand the test of time. This can include spacing between very intricate lettering and
            other small details usually present in a fine line tattoo. Please take our word that we only strive to give
            you the best tattoo, you will allow us to do. It&apos;s only because we care!!!
          </DisclaimerText>
          <CheckboxRow>
            <input
              id="readDisclaimer"
              type="checkbox"
              checked={readDisclaimer}
              onChange={e => {
                setReadDisclaimer(e.target.checked);
                if (e.target.checked) setDisclaimerError('');
              }}
            />
            <label htmlFor="readDisclaimer">
              I have read and understand this note about small / simple and fine line tattoos.
            </label>
          </CheckboxRow>
          {disclaimerError && <ErrorText>{disclaimerError}</ErrorText>}
        </DisclaimerBox>

            <ButtonRow>
              <PrimaryButton
                type="submit"
                disabled={submitting}
                style={
                  bookingKind === 'consultation'
                    ? {
                        backgroundColor: '#e5e7eb',
                        color: '#111827',
                        border: '1px solid #d1d5db',
                      }
                    : undefined
                }
              >
                {submitting
                  ? 'Sending...'
                  : bookingKind === 'consultation'
                  ? 'Submit consultation request'
                  : 'Submit booking request'}
              </PrimaryButton>
            </ButtonRow>

            {status && (
              <StatusMessage $type={status.type}>{status.message}</StatusMessage>
            )}
          </>
        )}
      </Form>
    </Container>
  );
};

export default Booking;
